import {
  EVIDENCE_STATUS_ICONS,
  EvidenceStatus,
  GetEvidenceStatusResponse,
} from "../evidence-status.types";
import {
  DropDownItem,
  DropDownMenu,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EvidenceStatusQuery } from "../evidence-status.query";
import { EvidenceStatusAPI } from "../evidence-status.api";
import { Check } from "lucide-react";
import { usePermissions } from "@/hooks";

const resolveIcon = (status?: EvidenceStatus) => {
  if (!status) return null;
  const Icon = EVIDENCE_STATUS_ICONS[status.icon];
  if (Icon) {
    return <Icon size={14} color={status.color} />;
  }
  return null;
};

export const EvidenceStatusBtnSelector: React.FC<{
  defaultValue?: string;
  value?: string;
  onChange?: (value: string, data: EvidenceStatus) => void;
}> = ({ defaultValue, value, onChange }) => {
  const theme = useTheme();
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const isDisabled = !hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_UPDATE);
  const isControlled = useRef(value !== undefined);
  const [stateValue, setStateValue] = useState<string | undefined>(
    defaultValue
  );
  const _value = isControlled.current ? value : stateValue;

  const { data } = useQuery<GetEvidenceStatusResponse>({
    queryKey: EvidenceStatusQuery.queryKeys.list(),
    queryFn: () => EvidenceStatusAPI.get(),
    staleTime: 10000, // 10 seconds
  });

  const handleChange = (value: DropDownItem) => {
    if (value.value === "") {
      setStateValue(undefined);
      onChange?.("", value.data as EvidenceStatus);
      return;
    }
    setStateValue(value.value as string);
    onChange?.(value.value as string, value.data as EvidenceStatus);
  };

  const dropdownOptions: DropDownItem[] = [
    ...(data?.data?.map((status) => {
      const statusIdString = status?.status_name;
      const isSelected = _value === statusIdString;

      return {
        value: status.status_name,
        label: status.status_name,
        data: status,
        rightSection: isSelected ? <Check size={14} /> : null,
        leftSection: resolveIcon(status),
      };
    }) ?? []),
  ];

  const selectedStatus = dropdownOptions?.find(
    (option) => option.value === _value
  );

  return (
    <DropDownMenu
      data={dropdownOptions}
      disabled={isDisabled}
      variant="contained"
      size="xs"
      onItemSelect={handleChange}
      buttonProps={{
        size: "xs",
        leftSection: resolveIcon(selectedStatus?.data),
        style: {
          backgroundColor: theme.name === "DARK" ? "#303030" : "#f0f0f0",
          color: theme.name === "DARK" ? theme.palette.text.primary : "#000000",
          opacity: selectedStatus ? 1 : 0.75,
          fontWeight: "normal",
          padding: "2px 5px",
          height: "24px",
          textDecoration: "none",
        },
      }}
      dropDownProps={{
        style: {
          width: 160,
        },
      }}
    >
      {selectedStatus ? selectedStatus.data?.status_name : "Select Status"}
    </DropDownMenu>
  );
};
